import { Component, Input } from '@angular/core';
import { PLUGINS, SERVICES, THEMES, COLOR_TOOL_KEYS, TOOLS } from 'src/app/data/product/product.data';
import { AI_TOOLS } from 'src/app/data/ai/ai-tools.data';
import { SERVICES_SHOWCASE } from '../../../data/product/services-showcase.data';
import { environment } from 'src/environments/environment';
import { environmentCommon } from 'src/environments/environment-common';
import { BRAND_PAGES } from '../../../data/brand-pages/brand-pages.data';
import { BRAND } from '../../../data/brand/brand.data';
import { COMPANY_PAGES, RESOURCES_PAGES } from '../../../data/company/company.data';
import { URLS } from '../../../data/navigation/navigation.data';
import { Product, ProductLine } from '../../../model/product/product.model';
import { ProductService } from '../../../service/product/product.service';
import { ThemeService } from '../../../service/theme/theme.service';

@Component( {
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: [ './footer.component.scss' ]
} )
export class FooterComponent {

  @Input() hideShadow = false;
  @Input() popupPadding = false;

  // the Apps & Plugins column: the Mac and iPhone apps first, then the browser and IDE plugins (owner 2026-10-12)
  private static readonly APP_ORDER = [ 'macos-apps', 'ios-apps', 'plugins', 'jetbrains-plugins', 'vscode-extensions' ];
  PLUGINS: ProductLine[] = [ ...PLUGINS ].sort( ( a, b ) =>
    FooterComponent.rank( a.key ) - FooterComponent.rank( b.key ) );
  // the themes' icons with the All In One bundle first, then the themes in their usual order (owner 2026-10-12)
  readonly FOOTER_THEMES: { product: Product; category: string }[] = THEMES
    .reduce( ( all, line ) => all.concat( line.products.map( product => ( { product, category: line.name } ) ) ),
      [] as { product: Product; category: string }[] )
    .sort( ( a, b ) => Number( b.product.key === 'all-in-one' ) - Number( a.product.key === 'all-in-one' ) );
  TOOLS: ProductLine[] = TOOLS;
  // the services in the order of the home's Our Services chips (owner 2026-10-12)
  SERVICES: ProductLine[] = SERVICES.map( line => ( {
    ...line,
    products: [ ...line.products ].sort( ( a, b ) => FooterComponent.serviceRank( a.key ) - FooterComponent.serviceRank( b.key ) ),
  } ) );

  // the Tools column in the Tools page's groups, AI Tools first (tools-page.component.ts), each tool as its icon; the
  // converters two rows of them, then All Converters (owner 2026-10-12)
  private static readonly ALL_TOOLS: Product[] = TOOLS[ 0 ]?.products ?? [];
  readonly TOOL_GROUPS: { name: string; tools: Product[]; limit?: number }[] = [
    { name: 'AI Tools', tools: AI_TOOLS },
    { name: 'Color Tools', tools: FooterComponent.ALL_TOOLS.filter( t => COLOR_TOOL_KEYS.includes( t.key ) ) },
    { name: 'Generators', tools: FooterComponent.ALL_TOOLS.filter( t => !t.key.includes( '-to-' ) && !COLOR_TOOL_KEYS.includes( t.key ) ) },
    { name: 'Converters', tools: FooterComponent.ALL_TOOLS.filter( t => t.key.includes( '-to-' ) ), limit: 12 },
  ];
  readonly themeCount = THEMES.reduce( ( n, line ) => n + line.products.length, 0 );

  // Company with the resources that are pages of their own; the legal pages go to the foot line
  private static readonly LEGAL_URLS = [ URLS.terms, URLS.storeTerms, URLS.privacyPolicy, URLS.cookiePolicy, URLS.sitemap ];
  // Color Palettes left the footer (owner 2026-10-12): the nav's Palettes covers it
  readonly COMPANY_LINKS = [ ...COMPANY_PAGES, ...RESOURCES_PAGES.filter( p => !FooterComponent.LEGAL_URLS.includes( p.url ) ) ]
    .filter( p => p.name !== 'Color Palettes' );
  readonly LEGAL_LINKS = RESOURCES_PAGES.filter( p => FooterComponent.LEGAL_URLS.includes( p.url ) )
    .map( p => ( { ...p, name: p.name === 'Terms And Conditions' ? 'Terms' : p.name } ) );

  BRAND = BRAND;
  URLS = URLS;
  BRAND_PAGES = BRAND_PAGES;

  appVersion = '1.0.0';

  environment = environment;
  environmentCommon = environmentCommon;

  constructor(
    private themeService: ThemeService,
    private productService: ProductService,
  ) {
    this.appVersion = themeService.appVersion;
  }

  private static serviceRank( key: string ): number {
    const i = SERVICES_SHOWCASE.slides.findIndex( slide => slide.key === key );
    return i < 0 ? SERVICES_SHOWCASE.slides.length : i;
  }

  private static rank( key: string ): number {
    const i = FooterComponent.APP_ORDER.indexOf( key );
    return i < 0 ? FooterComponent.APP_ORDER.length : i;
  }

  /** Crawlable destination for a product link, see ProductService.productUrl. */
  productHref( product: Product, productLine: ProductLine ): string {
    return this.productService.productUrl( product, productLine );
  }

  redirectToProductUrl( product: Product, productLine: ProductLine, event?: Event ) {
    // The anchor carries a real href for crawlers; navigation is handled in JS
    // (fragment scroll / external open), so suppress the default page load.
    event?.preventDefault();
    this.productService.redirectToProductUrl( product, productLine );
  }
}
